import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const cagnesSurMerEn: ChauffeurPage = {
  slug: "cagnes-sur-mer",
  title: "VTC & private chauffeur in Cagnes-sur-Mer",
  subtitle:
    "Private chauffeur in Cagnes-sur-Mer: Nice airport transfers, the racecourse, Haut-de-Cagnes and the beaches.",
  keywords: [
    "vtc cagnes-sur-mer",
    "private chauffeur cagnes-sur-mer",
    "cagnes racecourse chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Cagnes-sur-Mer",
    subtitle:
      "Nice airport transfers, the Côte d'Azur racecourse, Haut-de-Cagnes and the Cros beaches — available 24/7.",
    keywords: [
      "VTC Cagnes-sur-Mer",
      "Cagnes racecourse chauffeur",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Cagnes-sur-Mer and Haut-de-Cagnes",
    badges: [
      "Haut-de-Cagnes & racecourse",
      "Flight tracking",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Cagnes-sur-Mer",
    items: [
      {
        title: "Hilltop Haut-de-Cagnes",
        description:
          "Medieval village and the Grimaldi Castle-Museum: drop-off at the foot of the cobbled lanes, where the car can go no further.",
      },
      {
        title: "Côte d'Azur racecourse",
        description:
          "Race meetings and big days: coordinated drop-off and pick-up, group round-trips with no parking worry.",
      },
      {
        title: "Nice airport in ~12 min",
        description:
          "A direct link via the A8 or the seafront, with flight tracking and a personal welcome.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Cagnes-sur-Mer",
    services: [
      {
        title: "Nice airport transfers",
        text: "Cagnes-sur-Mer ⇄ Nice Côte d'Azur airport in about 12 minutes, with flight tracking and a terminal welcome.",
      },
      {
        title: "Côte d'Azur racecourse",
        text: "Race days, evening meetings and groups: drop-off as close as possible to the stands and a coordinated return.",
      },
      {
        title: "Haut-de-Cagnes & Renoir Museum",
        text: "Drop-off at the foot of the medieval village and a link to the Renoir Museum at Les Collettes and its olive grove.",
      },
      {
        title: "Cros-de-Cagnes & beaches",
        text: "Fishing village, seafront restaurants and beaches, with drop-off as close as possible.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day, a dedicated chauffeur for your trips along the Riviera.",
      },
    ],
  },
  locations: {
    heading: "Areas & pick-up points",
    items: [
      {
        name: "Haut-de-Cagnes",
        description: "Medieval hilltop village and the Grimaldi Castle-Museum.",
      },
      {
        name: "Côte d'Azur racecourse",
        description: "Racetrack, winter and summer meetings.",
      },
      {
        name: "Renoir Museum (Les Collettes)",
        description: "The painter's house-studio and olive grove.",
      },
      {
        name: "Cros-de-Cagnes",
        description: "Fishing village, harbour and beaches.",
      },
      {
        name: "Polygone Riviera",
        description: "Open-air shopping centre near the A8 exit.",
      },
      {
        name: "Nice airport",
        description: "~12 min via the A8 or the seafront.",
      },
    ],
  },
  generic: {
    heading: "Cagnes-sur-Mer, between Haut-de-Cagnes, racecourse and seafront",
    subheading:
      "From the medieval village to Cros-de-Cagnes, one chauffeur linking the town's three faces.",
    paragraphs: [
      "Cagnes-sur-Mer reads in three layers: Haut-de-Cagnes, a medieval hilltop village around the Grimaldi Castle-Museum, with cobbled lanes where the car soon gives way; the plain and its Côte d'Azur racecourse; and Cros-de-Cagnes, a former fishing village turned lively seafront.",
      "For Haut-de-Cagnes, the chauffeur drops you at the foot of the village and collects you at the same spot, so you never hunt for a space on the hairpins. The Renoir Museum at Les Collettes, with its house-studio and olive grove, follows on naturally.",
      "On race days at the racecourse, we coordinate drop-off and pick-up as close as possible to the stands, including for groups and evening meetings. About 12 minutes from Nice airport, Cagnes is also an ideal base for a quick transfer or a beach day at Cros-de-Cagnes.",
    ],
    bullets: [
      "Drop-off at the foot of Haut-de-Cagnes",
      "Coordinated race days",
      "Nice airport ~12 min",
    ],
  },
  process: {
    heading: "Book a chauffeur in Cagnes-sur-Mer",
    steps: [
      "Share your route or your addresses (airport, racecourse, Haut-de-Cagnes, beaches).",
      "We confirm the right vehicle and the fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Drop-off at the foot of the village, the stands or the seafront, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Cagnes-sur-Mer"),
    {
      q: "How long is the drive to Nice airport from Cagnes-sur-Mer?",
      a: "Allow about 12 minutes via the A8 or the seafront outside peak hours. We track your flight to adjust the pick-up time, both ways.",
    },
    {
      q: "Do you drive on race days at the racecourse?",
      a: "Yes. On meeting days at the Côte d'Azur racecourse, we drop off as close as possible to the stands and coordinate the pick-up, including for groups and evening races.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and on race days; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer in Cagnes-sur-Mer?",
      a: "Mercedes E-Class and S-Class sedans, a 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can you drive up to Haut-de-Cagnes by car?",
      a: "The cobbled lanes of the medieval village are narrow and largely pedestrian. The chauffeur drops you at the foot of the village, near the Grimaldi Castle-Museum, then collects you at the same spot.",
    },
  ],
  relatedLinks: [
    { label: "Private car service in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Chauffeur hire Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Cagnes-sur-Mer",
    text: "Racecourse, Haut-de-Cagnes or airport transfer — quick reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Cagnes-sur-Mer — private chauffeur & transfers",
    description:
      "Private car service in Cagnes-sur-Mer: Nice airport transfer (~12 min), the racecourse, Haut-de-Cagnes and the Cros beaches. 5.0/5 from 38 Google reviews.",
    canonical: "/en/chauffeur/cagnes-sur-mer",
  },
};

export default cagnesSurMerEn;
